import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/functions";

/**
 * Connecteur de la collection ZIMS (occupation des enclos).
 *
 * Données écrites par les cloud functions (zimsCollectionSync) :
 *  - zoo/{zooId}/zimsEnclosures/{enclosureGUID} : état courant, 1 doc par enclos ZIMS
 *  - zoo/{zooId}/zimsSnapshots/{snapshotId}     : 1 doc par sync (méta + diff)
 *  - zoo/{zooId}/zimsMovements/{eventId}        : propositions de mouvements (pending)
 *
 * Côté client on lit ces collections, on démarre une sync (callable) et on
 * résout les propositions (appliquer/ignorer). Aucune écriture automatique :
 * chaque application est un geste explicite de l'utilisateur.
 */
export class ZimsCollectionConnector {
  constructor() {
    this.db = firebase.firestore();
  }

  // ---- Lectures ----

  async _getZimsEnclosures(zooId) {
    const snapshot = await this.db
      .collection("zoo")
      .doc(zooId)
      .collection("zimsEnclosures")
      .get();
    const list = [];
    snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return list;
  }

  async _getSnapshots(zooId, limitCount = 10) {
    const snapshot = await this.db
      .collection("zoo")
      .doc(zooId)
      .collection("zimsSnapshots")
      .orderBy("createdAt", "desc")
      .limit(limitCount)
      .get();
    const list = [];
    snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
    return list;
  }

  /**
   * Propositions de mouvements en attente.
   * Filtre d'égalité seul (pas d'orderBy) pour éviter un index composite ;
   * tri côté client.
   */
  async _getPendingMovements(zooId) {
    const snapshot = await this.db
      .collection("zoo")
      .doc(zooId)
      .collection("zimsMovements")
      .where("status", "==", "pending")
      .get();
    const list = [];
    snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
    list.sort((a, b) => {
      const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return tb - ta;
    });
    return list;
  }

  /**
   * Écoute temps réel du nombre de propositions de mouvements en attente
   * (badge du menu latéral et de l'onglet). Retourne la fonction d'unsubscribe.
   */
  _subscribeToPendingMovementsCount(zooId, callback) {
    return this.db
      .collection("zoo")
      .doc(zooId)
      .collection("zimsMovements")
      .where("status", "==", "pending")
      .onSnapshot(
        (snapshot) => callback(snapshot.size),
        (error) => console.error("[ZimsCollection] movements badge error:", error)
      );
  }

  /**
   * Écoute temps réel d'un snapshot (running → done/failed).
   * Retourne la fonction d'unsubscribe.
   */
  _subscribeToSnapshot(zooId, snapshotId, callback) {
    return this.db
      .collection("zoo")
      .doc(zooId)
      .collection("zimsSnapshots")
      .doc(snapshotId)
      .onSnapshot(
        (doc) => callback(doc.exists ? { id: doc.id, ...doc.data() } : null),
        (error) => {
          console.error("[ZimsCollection] snapshot listener error:", error);
          callback(null);
        }
      );
  }

  // ---- Actions ----

  async _startSync(zooId) {
    const callable = firebase
      .app()
      .functions("europe-west3")
      .httpsCallable("startZimsCollectionSync", { timeout: 60000 });
    const response = await callable({ zooId });
    return response && response.data;
  }

  /**
   * Collection déposée en fichier (export Excel de ZIMS), traitée par le
   * serveur comme une synchro. Retourne le résumé du snapshot finalisé ; une
   * erreur porte `code` (INSTITUTION_REQUIRED, ALREADY_RUNNING…).
   */
  async _importFile(zooId, { animals, hasEnclosureColumn, unplacedLabel, fileName }) {
    const callable = firebase
      .app()
      .functions("europe-west3")
      .httpsCallable("importZimsCollectionFile", { timeout: 300000 });
    try {
      const response = await callable({
        zooId,
        fileName,
        collection: { animals, hasEnclosureColumn, unplacedLabel },
      });
      return response && response.data;
    } catch (error) {
      throw Object.assign(new Error(error.message), {
        code: error.details?.code || error.code || "importFailed",
      });
    }
  }

  /**
   * Applique une proposition de mouvement côté AWM :
   *  - departure : marque l'animal matché comme parti (isDeparted)
   *  - move      : déplace l'animal matché vers l'enclos AWM matché
   *  - arrival   : pas d'application ici ; l'arrivée se traite en important
   *                l'animal, puis _closeArrivalsForImportedAnimals la ferme
   * Puis marque la proposition comme appliquée.
   */
  async _applyMovement(zooId, movement, userId) {
    if (!movement.matchedAnimalId) {
      throw new Error("No matched AWM animal for this movement");
    }

    if (movement.type === "departure") {
      // Mêmes champs que le passage manuel à « parti » sur la fiche : l'onglet « Partis »
      // exige le parc dans authorizedZooIds (sinon l'animal ne figurait plus dans aucun
      // onglet, retour du 27/10/2026) et un animal parti n'occupe plus d'enclos.
      await this.db.collection("animals").doc(movement.matchedAnimalId).update({
        isDeparted: true,
        authorizedZooIds: firebase.firestore.FieldValue.arrayUnion(zooId),
        animalEnclosureData: null,
        animalBelongToEnclosure: false,
      });
    } else if (movement.type === "move") {
      if (!movement.toMatchedEnclosureId) {
        throw new Error("No matched AWM enclosure for this movement");
      }
      // animalEnclosureData est un trio {enclosureId, enclosureName, linkedSectorId} :
      // on le reconstruit depuis l'enclos AWM cible pour rester cohérent.
      const enclosureDoc = await this.db
        .collection("enclosures")
        .doc(movement.toMatchedEnclosureId)
        .get();
      if (!enclosureDoc.exists) {
        throw new Error("Target AWM enclosure not found");
      }
      const enclosure = enclosureDoc.data();
      await this.db.collection("animals").doc(movement.matchedAnimalId).update({
        animalEnclosureData: {
          enclosureId: movement.toMatchedEnclosureId,
          enclosureName: enclosure.enclosureName || enclosure.label || "",
          linkedSectorId: enclosure.linkedSectorId || null,
        },
      });
    } else {
      throw new Error(`Movement type "${movement.type}" cannot be applied automatically`);
    }

    await this._resolveMovement(zooId, movement.eventId, "applied", userId);
  }

  async _dismissMovement(zooId, eventId, userId) {
    await this._resolveMovement(zooId, eventId, "dismissed", userId);
  }

  /**
   * Une arrivée se traite en important l'animal : une fois importé, ses
   * propositions d'arrivée en attente sont marquées « appliquées ». Seules les
   * arrivées d'un animal encore inconnu d'AWM à leur création sont visées.
   * Retourne les eventId fermés.
   */
  async _closeArrivalsForImportedAnimals(zooId, gans, userId) {
    const imported = new Set((gans || []).map((gan) => String(gan || "").trim()).filter(Boolean));
    if (!zooId || !imported.size) return [];
    const pending = await this._getPendingMovements(zooId);
    const arrivals = pending.filter((movement) =>
      movement.type === "arrival" &&
      !movement.matchedAnimalId &&
      imported.has(String(movement.gan || "").trim())
    );
    await Promise.all(
      arrivals.map((movement) => this._resolveMovement(zooId, movement.eventId, "applied", userId))
    );
    return arrivals.map((movement) => movement.eventId);
  }

  async _resolveMovement(zooId, eventId, status, userId) {
    await this.db
      .collection("zoo")
      .doc(zooId)
      .collection("zimsMovements")
      .doc(eventId)
      .update({
        status,
        resolvedAt: firebase.firestore.FieldValue.serverTimestamp(),
        resolvedBy: userId || null,
      });
  }
}

export default ZimsCollectionConnector;
